import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { X } from 'lucide-react'
import { useDashboardStore } from '../../store/dashboardStore'

// Единая точка закрытия: сначала проигрывается анимация (карточка выезжает вниз),
// и только потом вызывается реальный onClose родителя. Любой close внутри
// модалки (кнопки, футер) должен вызывать modalRef.current?.close(), чтобы
// получить ту же анимацию.
//
// dismissable=false — блокирует «случайные» пути закрытия (тап по подложке,
// клавиша Esc). Используется на шаге оплаты, чтобы живую заявку нельзя было
// потерять нечаянным тапом/свайпом.
// onHeaderClose — кастомный обработчик для крестика ✕ (например, тот же
// флоу «Отмена» с подтверждением). Если не передан, ✕ закрывает модалку.
// Если модалка не dismissable и onHeaderClose не задан — ✕ отключается.
const Modal = forwardRef(function Modal({ title, onClose, children, footer, dismissable = true, onHeaderClose = null }, ref) {
  const setIsModalOpen = useDashboardStore(s => s.setIsModalOpen)
  const [closing, setClosing] = useState(false)
  const closingRef = useRef(false)

  const requestClose = () => {
    if (closingRef.current) return
    closingRef.current = true
    setClosing(true)
    setTimeout(() => onClose(), 280) // ≈ длительность slideDown
  }

  useImperativeHandle(ref, () => ({ close: requestClose }))

  useEffect(() => {
    setIsModalOpen(true)
    const handleKey = e => { if (e.key === 'Escape' && dismissable) requestClose() }
    window.addEventListener('keydown', handleKey)

    // Lock background scroll
    const root = document.getElementById('root')
    const prev = root?.style.overflow ?? ''
    if (root) root.style.overflow = 'hidden'

    return () => {
      setIsModalOpen(false)
      window.removeEventListener('keydown', handleKey)
      if (root) root.style.overflow = prev
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissable])

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingTop: 'calc(env(safe-area-inset-top) + 64px)' }}
      onClick={dismissable ? requestClose : undefined}
    >
      {/* Backdrop — без backdrop-filter: на iOS Safari размытие на position:fixed
          ломает порядок слоёв, и нижнее меню рисуется поверх модалки */}
      <div className={closing ? 'sheet-backdrop sheet-backdrop-out' : 'sheet-backdrop'} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.78)' }} />

      {/* Bottom sheet */}
      <div
        className={closing ? 'sheet-card sheet-out' : 'sheet-card'}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          background: '#141414',
          borderRadius: '22px 22px 0 0',
          maxWidth: 480,
          width: '100%',
          margin: '0 auto',
          zIndex: 1,
          maxHeight: '92dvh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderBottom: 'none',
        }}
      >
        {/* Handle */}
        <div style={{ width: 40, height: 4, background: '#333', borderRadius: 2, margin: '14px auto 0', flexShrink: 0 }} />

        {/* Header row — fixed */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0', flexShrink: 0 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>{title}</h3>
          <button
            onClick={onHeaderClose || requestClose}
            disabled={!dismissable && !onHeaderClose}
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: '#252525',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', cursor: (!dismissable && !onHeaderClose) ? 'default' : 'pointer',
              opacity: (!dismissable && !onHeaderClose) ? 0.4 : 1,
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            <X size={17} color="#8E8E93" />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{
          padding: '12px 20px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          overflowY: 'auto',
          flex: 1,
          WebkitOverflowScrolling: 'touch',
        }}>
          {children}
        </div>

        {/* Sticky footer (save / action button) */}
        {footer && (
          <div style={{
            padding: '12px 20px',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            flexShrink: 0,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
})

export default Modal
